import { FC, useState, ChangeEvent, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import { Layout } from '../../../containers'
import { Title, TextSummary, Select, Button } from '../../../components/UI'
import { DisplayDuration, ExamsLaboratory, ExamsOthers } from '../../../components'
import { useExams, useFamilyGroup, useAuthToken } from '../../../hooks'
import config from '../../../../config'

const tabClass = 'react-tabs__tab react-tabs_col-2'

const ExamsProceduresPage:FC = () => {
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [familyFilterSelected, setFamilyFilterSelected] = useState<string>('')
  const familyFilter = useFamilyGroup('FILTER')
  const { id } = useParams<{ id: string }>()
  const laboratoryExams = useExams()
  const relationshipTypes = useExams('RELATIONSHIP')
  const userToken = useAuthToken()
  const tableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTabIndexDefault(id)
  }, [id])

  const setTabIndexDefault = (id: string) => {
    if(id === 'laboratorio') {
        setTabIndex(0);
    } else if (id === 'otros-examenes') {
        setTabIndex(1);
    } else if (id === 'plazos-visualizacion') {
        setTabIndex(2);
    }

  }

  const onTabSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } } = event
    setTabIndex(Number(value))
  }

  const onFamilyFilterChange = (userId: string) => {
    setFamilyFilterSelected(userId)
    userToken.getUserTokenById(userId)
    laboratoryExams.getExams(userId, 1, 5)
  }

  const onPageChange = async (page: number) => {
    await laboratoryExams.getExams(familyFilterSelected, page, 5)
    
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      })
    }
  }

  // get url query parameters, get from_family_group
  const urlParams = new URLSearchParams(window.location.search);
  const fromFamilyGroup = urlParams.get('from_family_group');

  // get patientId from url
  const patientId = urlParams.get('patient_id');

  useEffect(() => {
    if(patientId) {
      onFamilyFilterChange(patientId)
    }
  }, [patientId])

  return (
    <Layout menu titleMain={7}>
      <Title border>
        {fromFamilyGroup ? 'Mi grupo familiar / Exámenes y procedimientos' : 'Mis Exámenes y procedimientos'}
      </Title>

      <TextSummary className="mt-30">
        {fromFamilyGroup ? 'En esta sección podrás ver y compartir el resultado de los exámenes de tu grupo familiar.' : 'En esta sección podrás ver y compartir el resultado de tus exámenes.'}<br />
      Los resultados de los exámenes de laboratorio estarán disponibles desde el 2017 en adelante, mientras que los de imagenología y procedimientos desde noviembre 2021 en adelante.</TextSummary>

      {fromFamilyGroup && (
        <Button 
          size="md" 
          color="primary" 
          className="mt-20 lg:mt-30" 
          component="link"
          to={`${config.ROUTES.DASHBOARD.FAMILYGROUP.HOME}`}
        >
          Volver a Mi Grupo Familiar
        </Button>
      )}

      <TextSummary className="exam_alert mt-30">
        <div className="row">
          <div className="alert-col-icon"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7.99996" cy="7.67592" r="6.83333" stroke="#0098D8"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.99992 7.00928C8.36811 7.00928 8.66659 7.30775 8.66659 7.67594V10.3426C8.66659 10.7108 8.36811 11.0093 7.99992 11.0093C7.63173 11.0093 7.33325 10.7108 7.33325 10.3426V7.67594C7.33325 7.30775 7.63173 7.00928 7.99992 7.00928Z" fill="#0098D8"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.33325 5.00926C7.33325 4.64107 7.63173 4.34259 7.99992 4.34259H8.00659C8.37477 4.34259 8.67325 4.64107 8.67325 5.00926C8.67325 5.37745 8.37477 5.67592 8.00659 5.67592H7.99992C7.63173 5.67592 7.33325 5.37745 7.33325 5.00926Z" fill="#0098D8"/></svg></div>
          <div className="alert-col-text">
            <strong>Examenes no disponibles en Mi Portal INDISA</strong>: Clasificación sanguínea ABO Rh - Test de Coombs Indirecto - Prueba de Antiglobulina Directa - Isoinmunizacion detección de anticuerpos - Titulación de Anticuerpos - VDRL (banco de sangre)
          </div>
        </div>
      </TextSummary>

      <Select
          name="tabOptions"
          sizes="md"
          color="primary"
          value={tabIndex}
          fullWidth
          className="lg:d-none mt-50"
          onChange={onTabSelectChange}
      >
        <option value="0">Laboratorio</option>
        <option value="1">Imágenes y procedimientos</option>
        <option value="2">Imágenes y procedimientos</option>
      </Select>

      <Tabs
          selectedIndex={tabIndex}
          className="lg:mt-25"
        onSelect={(index: number) => setTabIndex(index)}
      >
        <TabList>
          <Tab className={tabClass}>Laboratorio</Tab>
          <Tab className={tabClass}>Imágenes y procedimientos</Tab>
          <Tab className={tabClass}>Plazos de visualización</Tab>
        </TabList>

        <TabPanel>
          <div ref={tableRef}>
            <ExamsLaboratory
              data={laboratoryExams.data}
              loading={laboratoryExams.loading}
              relationshipTypes={relationshipTypes.data ? relationshipTypes.data : []}
              familyFilter={familyFilter}
              familyFilterSelected={familyFilterSelected}
              onFamilyFilterChange={onFamilyFilterChange}
              onPageChange={onPageChange}
              className="py-20 lg:px-30 xl:px-60"
            />
          </div>
        </TabPanel>
        <TabPanel style={{ background: '#e6e6e6' }}>
          <ExamsOthers
            userToken={userToken.data}
            familyFilter={familyFilter}
            familyFilterSelected={familyFilterSelected}
            onFamilyFilterChange={onFamilyFilterChange}
          />
        </TabPanel>
        <TabPanel>
          <DisplayDuration />
        </TabPanel>
      </Tabs>
    </Layout>
  )
}

export { ExamsProceduresPage }